import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Translate, Eye, EyeSlash } from '@phosphor-icons/react';

export default function Login() {
  const { login } = useAuth();
  const { t, lang, toggleLanguage } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-[#1f3e80] items-center justify-center overflow-hidden">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/25ec1707-6355-472f-8aac-fa008bbf6849/images/9b5f89e0356d8e1e8b233ba2d480198464b669e9e9df940a3ed078974973be98.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-10"
        />
        <div className="relative z-10 text-center px-12">
          <div className="mb-6">
            <span className="font-heading text-4xl font-bold text-white tracking-tight">eCRD</span>
          </div>
          <p className="text-lg text-white/80 font-body font-medium">Clinical Research Data Management</p>
          <p className="text-sm text-white/50 font-body mt-3 max-w-xs mx-auto">
            {lang === 'es'
              ? 'Sistema de gestion de datos de investigacion clinica para CRO'
              : 'Clinical research data management system for CRO'}
          </p>
          <div className="mt-8 flex items-center justify-center gap-3">
            {['GCP', 'ALCOA+', '21 CFR 11'].map(tag => (
              <span key={tag} className="text-[10px] font-mono text-white/60 border border-white/20 px-2.5 py-1 rounded-sm">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F9F8F6]">
        <div className="w-full max-w-sm">
          {/* Language toggle */}
          <div className="flex justify-end mb-10">
            <Button
              variant="ghost"
              size="sm"
              data-testid="login-language-toggle"
              onClick={toggleLanguage}
              className="text-xs font-mono text-stone-400 hover:text-stone-700 gap-1.5"
            >
              <Translate size={14} />
              {lang === 'es' ? 'English' : 'Espanol'}
            </Button>
          </div>

          {/* Header */}
          <div className="mb-8">
            <div className="mb-6">
              <img src="/logosgoyainnova.png" alt="Goya Innova" className="h-24" />
            </div>
            <div className="lg:hidden mb-6 flex items-center gap-3">
              <img src="/imagotipo.png" alt="Goya Innova" className="h-10" />
              <span className="font-heading text-3xl font-bold text-[#1f3e80]">eCRD</span>
            </div>
            <h2 className="text-2xl font-heading font-semibold text-stone-900 tracking-tight">{t('login')}</h2>
            <p className="text-sm text-stone-500 mt-1.5 font-body">{t('sign_in_subtitle')}</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            {error && (
              <div data-testid="login-error" className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-sm font-body">
                {error}
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-medium text-stone-800 font-body">{t('email')}</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-white border-stone-200 focus:border-[#1f3e80] focus:ring-1 focus:ring-[#1f3e80] rounded-sm"
                placeholder="admin@ecrd.com"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-sm font-medium text-stone-800 font-body">{t('password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-white border-stone-200 focus:border-[#1f3e80] focus:ring-1 focus:ring-[#1f3e80] rounded-sm pr-10"
                  placeholder="********"
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword(prev => !prev)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm h-10 font-body font-medium transition-colors duration-150"
            >
              {loading ? '...' : t('login')}
            </Button>
          </form>

          <p className="text-center text-xs text-stone-400 font-mono mt-8">
            eCRD CRO v1.0
          </p>
        </div>
      </div>
    </div>
  );
}
